import type { ReactElement } from 'react';
import React, { forwardRef, useRef } from 'react';
import type { KeyboardShortcut } from '@applied/utils/keyboard';
import classNames from 'classnames';

import './action-button.scss';

import { useDragonfruit } from '../../contexts/DragonfruitProvider';
import type { UserInteractionProps } from '../../utils/user_interactions';
import Icon, { type IconProps } from '../Icon/Icon';
import KeyboardShortcutLabel from '../KeyboardShortcutLabel/KeyboardShortcutLabel';
import ConditionalLink from '../Link/ConditionalLink';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

import BUTTON_CSS from './constants';

const ICON_SIZE = 14;

export type InternalActionButtonAppearance = 'section-button';
export type ActionButtonAppearance =
  | 'default'
  | 'primary'
  | 'invisible'
  | 'outline'
  | 'destructive'
  | 'success';
export type ActionButtonSize = 'small' | 'medium' | 'large';
export type ButtonProps = Omit<RootButtonProps, 'contentColor'>;

interface RootButtonProps extends UserInteractionProps {
  appearance?: ActionButtonAppearance | InternalActionButtonAppearance;
  size?: ActionButtonSize;
  disabled?: boolean;
  selected?: boolean;
  shouldFillContainer?: boolean;
  shouldCenterText?: boolean;
  iconBeforeProps?: IconProps;
  iconAfterProps?: IconProps;
  className?: string;

  onFilesChange?: (file: FileList) => void;
  /* If enabled, then users can select multiple files to upload via the file input. */
  allowUploadMultipleFiles?: boolean;

  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseUp?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  style?: React.CSSProperties;
  ariaLabel?: string;
  /**
   * Link to redirect to. Prefer setting `link` over specifying an `onClick` for links because
   * `link` will support right clicking to open in a new tab.
   *
   * Relative links (determined by whether the input starts with "/") will open in the current tab.
   * Absolute links will open in a new tab by default.
   */
  link?: string;
  loading?: boolean;
  contentColor?: string;

  /* Whether this button can be triggered via
   * hot key. This will be displayed in a lighter text
   * alongside the button name */
  keyboardShortcut?: KeyboardShortcut;
  buttonType?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
}

export const InternalActionButton = forwardRef(function InternalActionButton(
  {
    appearance = 'default',
    size = 'medium',
    shouldFillContainer = false,
    shouldCenterText = false,
    contentColor,
    iconBeforeProps,
    iconAfterProps,
    className = '',
    children,
    disabled: _disabled,
    selected,
    onFilesChange,
    allowUploadMultipleFiles = true,
    ariaLabel,
    link,
    loading,
    keyboardShortcut,
    trackingId,
    eventData,
    buttonType = 'button',
    ...otherProps
  }: RootButtonProps,
  ref: React.Ref<HTMLElement>,
): ReactElement<any, any> {
  const dragonfruit = useDragonfruit();
  const disabled = _disabled || loading;

  const props = {
    className: classNames(
      BUTTON_CSS.ROOT,
      className,
      `action-button-${appearance}`,
      `action-button-${size}`,
      {
        [BUTTON_CSS.SHOULD_FILL_CONTAINER]: shouldFillContainer,
        [BUTTON_CSS.DISABLED]: disabled,
        [BUTTON_CSS.SELECTED]: selected && !disabled,
        'action-button-icon-custom-color': iconBeforeProps?.color || iconAfterProps?.color,
      },
    ),
    ...otherProps,
  };

  const inputRef = useRef<HTMLInputElement>(null);

  // Setting the color via the style prop to keep the icon color and text color consistent.
  const style: React.CSSProperties = {};
  if (contentColor) {
    style.color = contentColor;
  }
  if (shouldCenterText) {
    style.textAlign = 'center';
  }

  // Set fallbacks for color and size for the icons.
  if (iconBeforeProps) {
    if (!iconBeforeProps.height) {
      iconBeforeProps.height = ICON_SIZE;
    }
    if (!iconBeforeProps.color) {
      iconBeforeProps.color = contentColor;
    }
  }

  if (iconAfterProps && !iconAfterProps.color) {
    if (!iconAfterProps.height) {
      iconAfterProps.height = ICON_SIZE;
    }
    if (!iconAfterProps.color) {
      iconAfterProps.color = contentColor;
    }
  }

  return (
    <ConditionalLink link={link} disabled={disabled} shouldFillContainer={shouldFillContainer}>
      <button
        aria-label={ariaLabel}
        type={buttonType}
        ref={ref as any}
        {...props}
        onClick={(evt): void => {
          if (!disabled) {
            props.onClick?.(evt);
            if (onFilesChange) {
              inputRef.current?.click();
            }
          }
          dragonfruit?.trackUserInteraction?.({
            componentInteractionId: 'dragonfruit-action-button-click',
            trackingId,
            eventData: {
              ariaLabel,
              ...eventData,
            },
          });
        }}
        data-testid="action-button"
      >
        {onFilesChange && (
          <input
            type="file"
            data-testid="button-file-input"
            ref={inputRef}
            onChange={(evt): void => {
              if (!evt.target.files?.length) {
                return;
              }
              onFilesChange(evt.target.files);
            }}
            multiple={allowUploadMultipleFiles}
          />
        )}
        {loading && <LoadingSpinner size={ICON_SIZE} shouldCenterVertically />}
        {iconBeforeProps?.src && <Icon {...iconBeforeProps} />}
        <div className="button-text-wrapper">
          {children !== undefined && (
            <div className="children-wrapper" style={style}>
              {children}
            </div>
          )}
          {keyboardShortcut && <KeyboardShortcutLabel shortcut={keyboardShortcut} />}
        </div>
        {iconAfterProps?.src && <Icon {...iconAfterProps} />}
      </button>
    </ConditionalLink>
  );
});

// The Default Button does not allow an iconAfter
const ActionButton = (props: ButtonProps): ReactElement<any, any> => {
  const { ...otherProps } = props;
  return (
    <InternalActionButton
      {...{
        ...otherProps,
      }}
    />
  );
};

export default ActionButton;
